import { Clock } from "lucide-react";
import DatePicker from "@/components/common/date-picker";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CouponDateTimeFieldProps = {
  /** ISO datetime string (or null/undefined for empty). */
  value?: string | null;
  onChange: (iso: string | null) => void;
  placeholder?: string;
  /** Days before this date are disabled in the calendar. */
  minDate?: Date;
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 00,05..55

/**
 * Coupon validity needs a date AND a time, but the shared <DatePicker> is
 * deliberately date-only — so the time picker is implemented here, in the
 * feature that actually requires it, and injected into the date picker's own
 * popover via its `footer` slot (below the calendar, centered). Both halves
 * edit a single ISO datetime string (the form field's source of truth), so
 * the calendar, the time selects and the trigger label can never drift.
 */
export default function CouponDateTimeField({
  value,
  onChange,
  placeholder = "Pick a date",
  minDate,
}: CouponDateTimeFieldProps) {
  // Guard against unparseable stored values — an Invalid Date would otherwise
  // crash date formatting on render.
  const parsed = value ? new Date(value) : undefined;
  const selected = parsed && !isNaN(parsed.getTime()) ? parsed : undefined;

  const hour12 = selected ? selected.getHours() % 12 || 12 : undefined;
  const minute = selected ? selected.getMinutes() : undefined;
  const meridiem = selected
    ? selected.getHours() < 12
      ? "AM"
      : "PM"
    : undefined;

  // Odd minutes (e.g. 07 or 23 from a legacy value) aren't in the 5-min grid —
  // include them so the select never shows blank for an existing coupon.
  const minuteOptions =
    minute !== undefined && !MINUTES.includes(minute)
      ? [...MINUTES, minute].sort((a, b) => a - b)
      : MINUTES;

  /** Base date for time edits: the picked day, or today at 00:00 if none yet. */
  const baseDate = () => {
    if (selected) return new Date(selected);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const handleDate = (day: Date | null) => {
    if (!day) {
      onChange(null);
      return;
    }
    const next = new Date(day);
    // Keep the already-chosen time when switching days.
    if (selected) {
      next.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
    } else {
      next.setHours(0, 0, 0, 0);
    }
    onChange(next.toISOString());
  };

  const setTime = (h12: number, m: number, ap: "AM" | "PM") => {
    const next = baseDate();
    const h24 = ap === "PM" ? (h12 % 12) + 12 : h12 % 12;
    next.setHours(h24, m, 0, 0);
    onChange(next.toISOString());
  };

  const currentYear = new Date().getFullYear();

  return (
    <DatePicker
      value={selected}
      onChange={handleDate}
      placeholder={placeholder}
      labelFormat="d MMM yyyy, h:mm a"
      defaultMonth={selected ?? minDate}
      disabled={minDate ? { before: minDate } : undefined}
      fromYear={currentYear - 10}
      toYear={currentYear + 10}
      footer={({ close }) => (
        <>
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Select
            value={hour12 !== undefined ? String(hour12) : undefined}
            onValueChange={(h) =>
              setTime(Number(h), minute ?? 0, meridiem ?? "AM")
            }
          >
            <SelectTrigger className="h-8 w-[64px]">
              <SelectValue placeholder="hh" />
            </SelectTrigger>
            <SelectContent className="max-h-56 min-w-[64px]">
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {String(h).padStart(2, "0")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">:</span>
          <Select
            value={minute !== undefined ? String(minute) : undefined}
            onValueChange={(m) =>
              setTime(hour12 ?? 12, Number(m), meridiem ?? "AM")
            }
          >
            <SelectTrigger className="h-8 w-[64px]">
              <SelectValue placeholder="mm" />
            </SelectTrigger>
            <SelectContent className="max-h-56 min-w-[64px]">
              {minuteOptions.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {String(m).padStart(2, "0")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={meridiem}
            onValueChange={(ap) =>
              setTime(hour12 ?? 12, minute ?? 0, ap as "AM" | "PM")
            }
          >
            <SelectTrigger className="h-8 w-[68px]">
              <SelectValue placeholder="AM" />
            </SelectTrigger>
            <SelectContent className="min-w-[68px]">
              <SelectItem value="AM">AM</SelectItem>
              <SelectItem value="PM">PM</SelectItem>
            </SelectContent>
          </Select>
          <Button type="button" size="sm" className="ml-1" onClick={close}>
            Done
          </Button>
        </>
      )}
    />
  );
}
